import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { BASE_URL } from '../config';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, className = "", disabled = false }) => (
  <button
    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    } ${className}`}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const CookieManager = () => {
  const [cookies, setCookies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCookie, setSelectedCookie] = useState(null);
  const [newCookieName, setNewCookieName] = useState('');
  const [newCookieJson, setNewCookieJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const { apiKey } = useAuth();

  useEffect(() => {
    fetchCookies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCookies = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/scout/cookies`, {
        headers: {
          'Authorization': `Bearer ${apiKey.key}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCookies(data);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to fetch cookies');
      }
    } catch (error) {
      console.error('Error fetching cookies:', error);
      setError('Failed to fetch cookies. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCookie = async () => {
    try {
      // Validate JSON
      let cookieJson;
      try {
        cookieJson = JSON.parse(newCookieJson);
        setJsonError('');
      } catch (e) {
        setJsonError('Invalid JSON format');
        return;
      }

      const response = await fetch(`${BASE_URL}/scout/cookies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey.key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newCookieName,
          cookies: cookieJson
        })
      });

      if (response.ok) {
        setIsCreateModalOpen(false);
        setNewCookieName('');
        setNewCookieJson('');
        fetchCookies();
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to create cookie');
      }
    } catch (error) {
      console.error('Error creating cookie:', error);
      setError('Failed to create cookie. Please check your input.');
    }
  };

  const handleViewCookie = async (cookieId) => {
    try {
      const response = await fetch(`${BASE_URL}/scout/cookies/${cookieId}`, {
        headers: {
          'Authorization': `Bearer ${apiKey.key}`
        }
      });

      if (response.ok) {
        const cookie = await response.json();
        setSelectedCookie(cookie);
        setNewCookieName(cookie.name);
        setNewCookieJson(JSON.stringify(cookie.cookie, null, 2));
        setIsEditModalOpen(true);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to fetch cookie details');
      }
    } catch (error) {
      console.error('Error fetching cookie details:', error);
      setError('Failed to fetch cookie details. Please check your connection.');
    }
  };

  const handleUpdateCookie = async () => {
    if (!selectedCookie) return;

    try {
      // Validate JSON
      let cookieJson;
      try {
        cookieJson = JSON.parse(newCookieJson);
        setJsonError('');
      } catch (e) {
        setJsonError('Invalid JSON format');
        return;
      }

      const response = await fetch(`${BASE_URL}/scout/cookies/${selectedCookie.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${apiKey.key}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newCookieName,
          cookies: cookieJson
        })
      });

      if (response.ok) {
        setIsEditModalOpen(false);
        setSelectedCookie(null);
        setNewCookieName('');
        setNewCookieJson('');
        fetchCookies();
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to update cookie');
      }
    } catch (error) {
      console.error('Error updating cookie:', error);
      setError('Failed to update cookie. Please check your input.');
    }
  };

  const handleDeleteCookie = async (cookieId) => {
    if (!window.confirm('Are you sure you want to delete this cookie?')) return;

    try {
      const response = await fetch(`${BASE_URL}/scout/cookies/${cookieId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${apiKey.key}`
        }
      });

      if (response.ok) {
        fetchCookies();
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to delete cookie');
      }
    } catch (error) {
      console.error('Error deleting cookie:', error);
      setError('Failed to delete cookie. Please check your connection.');
    }
  };

  const filteredCookies = cookies.filter(cookie => 
    cookie.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Cookie Manager</h1>
          <Button onClick={() => setIsCreateModalOpen(true)}>+ New Cookie</Button>
        </div>

        <Card>
          <div className="p-6">
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search cookies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="mt-2 text-gray-600">Loading cookies...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">
                {error}
              </div>
            ) : filteredCookies.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                {searchQuery ? 'No cookies found matching your search.' : 'No cookies found. Create one using the + New Cookie button.'}
              </div>
            ) : (
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {filteredCookies.map((cookie) => (
                  <div key={cookie.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <h3 className="font-semibold text-lg mb-2">{cookie.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">ID: {cookie.id}</p>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => handleViewCookie(cookie.id)}
                        className="!bg-blue-600"
                      >
                        View/Edit
                      </Button>
                      <Button 
                        onClick={() => handleDeleteCookie(cookie.id)}
                        className="!bg-red-600"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsCreateModalOpen(false);
              setNewCookieName('');
              setNewCookieJson('');
              setJsonError('');
            }
          }}
        >
          <Card className="w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Create New Cookie</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cookie Name
                  </label>
                  <input
                    type="text"
                    value={newCookieName}
                    onChange={(e) => setNewCookieName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter cookie name..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cookie JSON
                  </label>
                  <textarea
                    value={newCookieJson}
                    onChange={(e) => setNewCookieJson(e.target.value)}
                    className={`w-full h-48 p-2 border rounded-md resize-none ${
                      jsonError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Paste cookie JSON here..."
                  />
                  {jsonError && (
                    <p className="mt-1 text-sm text-red-600">{jsonError}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-2">
                  <Button 
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setNewCookieName('');
                      setNewCookieJson('');
                      setJsonError('');
                    }} 
                    className="!bg-gray-500"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateCookie}
                    disabled={!newCookieName || !newCookieJson}
                  >
                    Create
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && selectedCookie && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsEditModalOpen(false);
              setSelectedCookie(null);
              setNewCookieName('');
              setNewCookieJson('');
              setJsonError('');
            }
          }}
        >
          <Card className="w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Edit Cookie</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cookie Name
                  </label>
                  <input
                    type="text"
                    value={newCookieName}
                    onChange={(e) => setNewCookieName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter cookie name..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cookie JSON
                  </label>
                  <textarea
                    value={newCookieJson}
                    onChange={(e) => setNewCookieJson(e.target.value)}
                    className={`w-full h-48 p-2 border rounded-md resize-none ${
                      jsonError ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Paste cookie JSON here..."
                  />
                  {jsonError && (
                    <p className="mt-1 text-sm text-red-600">{jsonError}</p>
                  )}
                </div>

                <div className="text-sm text-gray-600">
                  <p>Last Updated: {new Date(selectedCookie.updated_at).toLocaleString()}</p>
                  <p>Created: {new Date(selectedCookie.created_at).toLocaleString()}</p>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button 
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedCookie(null);
                      setNewCookieName('');
                      setNewCookieJson('');
                      setJsonError('');
                    }} 
                    className="!bg-gray-500"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateCookie}
                    disabled={!newCookieName || !newCookieJson}
                  >
                    Update
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CookieManager; 